import * as React from 'react'
import { supabase } from './supabase'

type LiveCount = { count: number; checked_at: string }
type State = { result: LiveCount | null; status: 'loading' | 'ready' | 'unavailable' }

export function LiveUsersMetric({ active }: { active: boolean }) {
  const [state, setState] = React.useState<State>({ result: null, status: 'loading' })

  React.useEffect(() => {
    if (!active) return
    let disposed = false
    let interval: ReturnType<typeof setInterval> | undefined
    let request: { controller: AbortController; stale: boolean } | undefined
    let resumePending = false
    const visible = () => document.visibilityState === 'visible'
    const refresh = async () => {
      if (disposed || !visible() || request) return
      const current = { controller: new AbortController(), stale: false }
      request = current
      const timeout = setTimeout(() => current.controller.abort(), 5000)
      const canUpdate = () => !disposed && !current.stale && visible()
      try {
        const { data, error } = await supabase.rpc('admin_live_user_count').abortSignal(current.controller.signal)
        if (error || current.controller.signal.aborted || !data || !Number.isSafeInteger(data.count) || data.count < 0 || typeof data.checked_at !== 'string' || !Number.isFinite(Date.parse(data.checked_at))) {
          throw new Error('Live count unavailable')
        }
        if (canUpdate()) setState({ result: data as LiveCount, status: 'ready' })
      } catch {
        if (canUpdate()) setState({ result: null, status: 'unavailable' })
      } finally {
        clearTimeout(timeout)
        request = undefined
        if (resumePending) { resumePending = false; void refresh() }
      }
    }
    const sync = () => {
      clearInterval(interval)
      interval = undefined
      resumePending = false
      if (!visible()) {
        if (request) { request.stale = true; request.controller.abort() }
        return
      }
      setState({ result: null, status: 'loading' })
      if (request) resumePending = true
      else void refresh()
      interval = setInterval(() => void refresh(), 15_000)
    }
    document.addEventListener('visibilitychange', sync)
    sync()
    return () => {
      disposed = true
      resumePending = false
      clearInterval(interval)
      request?.controller.abort()
      document.removeEventListener('visibilitychange', sync)
    }
  }, [active])

  return <article className="metric">
    <small>Live checkouts</small>
    <strong>{state.result ? state.result.count.toLocaleString('en-GB') : '—'}</strong>
    {state.status !== 'ready' && <span className="metric-note" role="status">{state.status === 'loading' ? 'Loading…' : 'Live count unavailable'}</span>}
  </article>
}
